import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, useContext } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import moment from 'moment';
import { add } from '../../features/referenceBook/referenceBookSlice';
import {
  addFormData,
  addJob,
  addClient,
  addWorkHistory,
  setDatesToConfirm,
  setAdHocDates,
  setIsConfirmed,
  setNewNotes,
  setNewFiles,
  setLevyEnabled,
  setLevyValue,
  setEditedId,
  saveDatesForBooking,
  addShift,
  resetForm,
} from '../../features/bookingForm/bookingFormSlice';
import api from '../../config/api';
import ApplicantSection from '../../components/ApplicantSection/ApplicantSection';
import JobSection from '../../components/JobSection/JobSection';
import ClientSection from '../../components/ClientSection/ClientSection';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import Select from '../../components/Select/Select';
import {
  saveAsDraftSuccess,
  createSuccess,
  emailSendSuccess,
  error,
} from '../../helpers/notyf';
import css from './BookingFormPage.module.css';
import SendEmailModal from '../../components/JobSection/SendEmailModal/SendEmailModal';
import { debugApplicantPayload } from '../../utils/debugApplicant';

const props = {
  color: 'var(--primary)',
  height: 100,
  width: 110,
  secondaryColor: 'var(--primary)',
};

const BookingFormPage = () => {
  const [id, setId] = useState('');
  const [jobId, setJobId] = useState('');
  const [previousJob, setPreviousJob] = useState();
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [placementProcessing, setPlacementProcessing] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);
  const placementPollIntervalRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [checked, setChecked] = useState({ applicant: false, client: false });
  const [emailData, setEmailData] = useState({});
  const [user, setUser] = useState({});
  const [isAvailability, setIsAvailability] = useState(false);

  const formData = useSelector(state => state.bookingForm.formData);
  const files = useSelector(state => state.bookingForm.files);
  const notes = useSelector(state => state.bookingForm.notes);
  const dates = useSelector(
    state => state.bookingForm.formData?.job?.booked_data?.dates,
  );
  const pay_rates = useSelector(
    s => s.bookingForm.formData?.job?.booked_data?.pay_rates ?? []
  );
  const type = useSelector(state => state.bookingForm.dataType);
  const workHistory = useSelector(state => state.bookingForm.workHistory);
  const levyEnabled = useSelector(state => state.bookingForm.levyEnabled);
  const levyValue = useSelector(state => state.bookingForm.levyValue);
  const job = useSelector(
    s => s.bookingForm.formData?.job ?? {}
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const crmStatus         = useSelector(s => s.bookingForm.formData?.applicant?.status?.description);
  const complianceStatus  = useSelector(s => s.bookingForm.formData?.applicant?.file_sign_off_status);
  const complianceExpiry  = useSelector(s => s.bookingForm.formData?.applicant?.expiry_date);

  /**
   * Handle errors 
   * if any section Applicants || clients || jobs 
   * has error
   */
  const [hasApplicantErrors, setHasApplicantErrors] = useState(false);
  const [hasJobErrors, setHasJobErrors] = useState(false);
  const [hasClientErrors, setHasClientErrors] = useState(false);
  const hasAnyErrors = hasApplicantErrors || hasJobErrors || hasClientErrors;

  useEffect(() => {
    if (params.external_id) {
      setId(params.external_id);
      setIsAvailability(true);
      handleApplicantById(params.external_id);
    }
  }, [params.external_id]);

  useEffect(() => {
    localStorage.getItem('ACCESS_TOKEN') &&
      api.AUTH_ME().then(response => setUser(response));
  }, []);

  useEffect(() => {
    if (!params.id && !params.external_id) {
      // SECURITY: Reset form completely when creating a new form
      // This prevents old form data from persisting when navigating to a new form
      // Previously, addFormData({}) was merging with existing state, keeping old applicant/job/client data
      dispatch(resetForm());
      setId('');
      setJobId('');
      setClientId('');
      setPreviousJob(null);
      setIsAvailability(false);

      return;
    }
    if (params.id) {
      setLoading(true);
      api
        .SHOW_FORM(params.id)
        .then(response => {
          if (response?.status && response.status === 'OK') {
            // SECURITY: Validate response data before using it
            if (!response.data) {
              error({ message: 'Form data is missing. Please try again or contact support.' });
              setLoading(false);
              return;
            }
            
            // SECURITY: Check if form is being edited by another user
            // Only check if user.id is available (user might not be loaded yet)
            if (response.data.edited_by && user.id && response.data.edited_by !== user.id) {
              // Still load the form but show warning - don't block access
              console.warn(`Form ${params.id} is being edited by user ${response.data.edited_by}`);
            }
            
            dispatch(addFormData(response.data));
            const { files, notes, booked_data = {} } = response?.data?.job ?? {};
            const { enabled, value } = booked_data?.levy ?? {};
            if ((booked_data?.dates.length ?? []) === 0) {
              dispatch(setAdHocDates([]));
            }
            dispatch(setNewNotes(notes || ''));
            dispatch(setNewFiles(files));
            dispatch(setLevyEnabled(enabled));
            dispatch(setLevyValue(value || 1.5));
            
            // SECURITY: Check if applicant data exists before accessing external_id
            if (response.data.applicant?.external_id) {
              setId(response.data.applicant.external_id);
            } else {
              console.error('Applicant external_id is missing in form data');
              error({ message: 'Applicant data is incomplete. Please try again or contact support.' });
            }
            
            // If form is processing and has no placement, start polling
            if (response.data.status === 'processing' && !response.data.placement_external_id) {
              setPlacementProcessing(true);
              // Poll for placement creation
              let pollCount = 0;
              const maxPolls = 20;
              placementPollIntervalRef.current = setInterval(() => {
                pollCount++;
                api.SHOW_FORM(params.id)
                  .then(pollResponse => {
                    if (pollResponse?.status === 'OK' && pollResponse.data) {
                      dispatch(addFormData(pollResponse.data));
                      
                      // Check if placement was created
                      if (pollResponse.data.placement_external_id) {
                        if (placementPollIntervalRef.current) {
                          clearInterval(placementPollIntervalRef.current);
                          placementPollIntervalRef.current = null;
                        }
                        setPlacementProcessing(false);
                      } else if (pollResponse.data.status === 'error' || pollResponse.data.status === 'sent') {
                        if (placementPollIntervalRef.current) {
                          clearInterval(placementPollIntervalRef.current);
                          placementPollIntervalRef.current = null;
                        }
                        setPlacementProcessing(false);
                      }
                    }
                  })
                  .catch(() => {
                    if (placementPollIntervalRef.current) {
                      clearInterval(placementPollIntervalRef.current);
                      placementPollIntervalRef.current = null;
                    }
                    setPlacementProcessing(false);
                  });
                
                // Stop polling after max attempts
                if (pollCount >= maxPolls) {
                  if (placementPollIntervalRef.current) {
                    clearInterval(placementPollIntervalRef.current);
                    placementPollIntervalRef.current = null;
                  }
                  setPlacementProcessing(false);
                  setQueuePosition(null);
                }
              }, 3000);
            }
          } else {
            // SECURITY: Handle non-OK responses
            const errorMessage = response?.message || response?.data?.message || 'Failed to load form. Please try again or contact support.';
            error({ message: errorMessage });
            console.error('SHOW_FORM returned non-OK status:', response);
            setLoading(false);
          }
        })
        .catch(err => {
          // SECURITY: Handle API errors gracefully
          console.error('Error loading form:', err);
          // The API config already shows error notification, but we need to ensure loading state is cleared
          // Check if it's a specific error we should handle
          if (err?.response?.status === 404) {
            error({ message: 'Form not found. It may have been deleted or you may not have permission to view it.' });
          } else if (err?.response?.status === 403) {
            error({ message: 'You do not have permission to view this form.' });
          } else if (err?.response?.status === 401) {
            // Already handled by API config (redirects to login)
            return;
          } else {
            // Generic error - API config already shows notification
            error({ message: 'Failed to load form. Please refresh the page or contact support if the problem persists.' });
          }
          setLoading(false);
        })
        .finally(() => {
          // Ensure loading is always cleared
          setLoading(false);
        });
    }
  }, [params.id]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (placementPollIntervalRef.current) {
        clearInterval(placementPollIntervalRef.current);
        placementPollIntervalRef.current = null;
      }
      setPlacementProcessing(false);
    };
  }, []);

  useEffect(() => {
    dispatch(setEditedId(params.id));
    const handleBeforeUnload = async event => {
      event.preventDefault();
      if (params.id) {
        await api.CHANGE_EDITING(params.id);
        dispatch(setEditedId(null));
      }
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    api.GET_REF_BOOK().then(response => dispatch(add(response)));

    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, []);

  const alertUser = e => {
    e.preventDefault();
    e.returnValue =
      'Are you sure you want to leave this page? All unsave changes will be lost!';
  };

  const handleApplicantIdChange = e => {
    if (e.target.value.length > 6 || isNaN(+e.target.value)) return;
    setId(+e.target.value);
  };

  const datesAvailability = useSelector(
    state => state.bookingForm.datesForBooking,
  );

  function formatTime(time) {
    let timeStr = time.toString();
    if (time < 100) {
      timeStr = timeStr.padStart(2, '0') + '00';
    } else {
      timeStr = timeStr.padStart(4, '0');
    }
    return timeStr.slice(0, 2) + ':' + timeStr.slice(2);
  }

  const handleApplicantById = external_id => {
    setLoading(true);
    dispatch(addFormData({}));
    dispatch(addWorkHistory([]));
    dispatch(setDatesToConfirm([]));
    dispatch(setAdHocDates([]));
    dispatch(setIsConfirmed(false));
    setClientId('');
    setJobId('');
    api
      .GET_APPLICANT(id || external_id)
      .then(applicant => {
        if (applicant?.status === 'OK') {
          debugApplicantPayload('GET_APPLICANT (raw)', applicant.data);

          const booked = applicant.data?.job?.booked_data;
          if (external_id && booked) {
            if (
              applicant.data &&
              applicant.data.job &&
              applicant.data.job.booked_data
            ) {
              const { minStartTime, maxEndTime } = datesAvailability.reduce(
                (acc, item) => {
                  if (
                    !acc.minStartTime ||
                    new Date(item.start_time) < new Date(acc.minStartTime)
                  ) {
                    acc.minStartTime = item.start_time;
                  }
                  if (
                    !acc.maxEndTime ||
                    new Date(item.end_time) > new Date(acc.maxEndTime)
                  ) {
                    acc.maxEndTime = item.end_time;
                  }
                  return acc;
                },
                { minStartTime: null, maxEndTime: null },
              );
              if (type === 'ad_hoc') {
                applicant.data.job.booked_data.dates = datesAvailability;
                applicant.data.job.end_date = maxEndTime;
                applicant.data.job.start_date = minStartTime;
                applicant.data.job.booked_data.type = type;
              }
              if (type === 'standard') {
                const day_shifts = [...(booked.day_shifts ?? [])];
                booked.dates = datesAvailability ?? [];
                datesAvailability.forEach(elem => {
                  const index = day_shifts.findIndex(
                    item => item.day_name === moment(elem.date).format('dddd'),
                  );

                  const obj = {
                    day_name: moment(elem.date).format('dddd'),
                    date: elem.date,
                    shift_external_id: elem.shift_external_id,
                    overtime_rate_external_id: null,
                    hours: formatTime(elem.hours),
                    start_time: new Date(
                      moment().format('YYYY-MM-DD') +
                        'T' +
                        moment(elem.start_time).format('HH:mm:ss'),
                    ),
                    end_time: new Date(
                      moment().format('YYYY-MM-DD') +
                        'T' +
                        moment(elem.end_time).format('HH:mm:ss'),
                    ),
                    purchase_order: elem.purchase_order,
                    ward: elem.ward,
                  };
                  day_shifts.splice(index, 1, obj);
                });
                applicant.data.job.booked_data.day_shifts = day_shifts;
                applicant.data.job.end_date = maxEndTime;
                applicant.data.job.start_date = minStartTime;
              }
            }
          }
          // Normalize pay_type from object to integer before adding to form data
          // This ensures pay_type is always an integer in Redux state, not an object
          const normalizedApplicantData = {
            ...applicant.data,
            applicant: {
              ...applicant.data.applicant,
              pay_type: applicant.data.applicant?.pay_type?.id ?? 
                        (typeof applicant.data.applicant?.pay_type === 'number' ? applicant.data.applicant.pay_type : null) ??
                        applicant.data.applicant?.pay_type,
            },
          };
          
          dispatch(addFormData(normalizedApplicantData));
          debugApplicantPayload('AFTER addFormData(applicant)', normalizedApplicantData);

          api.GET_WORK_HISTORY(id || external_id).then(response => {
            if (response?.status && response.status === 'OK') {
              if (!response.data) {
                return;
              }
              dispatch(addWorkHistory(response.data));
              
              // Use normalizedApplicantData since pay_type is already normalized
              const cleaned = {
                ...normalizedApplicantData,
                applicant: {
                  ...normalizedApplicantData.applicant,
                  // pay_type is already normalized, but ensure it's still an integer
                  pay_type: normalizedApplicantData.applicant?.pay_type?.id ?? 
                            (typeof normalizedApplicantData.applicant?.pay_type === 'number' ? normalizedApplicantData.applicant.pay_type : null) ??
                            normalizedApplicantData.applicant?.pay_type,
                },
              };

              if (cleaned.job) {
                cleaned.job = {
                  ...cleaned.job,
                  pay_type_external_id: cleaned.job.pay_type_external_id != null
                    ? Number(cleaned.job.pay_type_external_id)
                    : null,
                };
              }
              
              // Guard against empty work history
              const firstWH = Array.isArray(response.data) ? response.data[0] : null;
              if (!firstWH?.placement_external_id) {
                console.warn('[BF] No placement_external_id in work history – skipping SET_AND_GET_DATA_BY_PLACEMENT_ID');
                return;
              }

              api
                // .SET_AND_GET_DATA_BY_PLACEMENT_ID(
                //   cleaned,
                //   response.data[0].placement_external_id,
                // )
                .SET_AND_GET_DATA_BY_PLACEMENT_ID(cleaned, firstWH.placement_external_id)
                .then(response => {
                  dispatch(addClient(response.data.client));

                  if (response?.status && response.status === 'OK') {
                    if (external_id) {
                      if (
                        applicant.data.job &&
                        applicant.data.job.booked_data
                      ) {
                        applicant.data.job.booked_data.dates =
                          datesAvailability;
                        applicant.data.job.booked_data.type = 'ad_hoc';
                        dispatch(saveDatesForBooking([]));
                      }
                    }
                    dispatch(addFormData(response.data));
                    debugApplicantPayload('AFTER addFormData(placement-prefill)', response.data);

                  }
                });
            }
          });
        }
      })
      .finally(() => setLoading(false));
  };

  const handleJobChange = e => {
    setLoading(true);
    setPreviousJob(+e.target.value);
    api
      .SET_AND_GET_DATA_BY_PLACEMENT_ID({ ...formData }, +e.target.value)
      .then(response => {
        if (response?.status && response.status === 'OK') {
          dispatch(addJob(response.data.job));
          dispatch(addClient(response.data.client));
        }
      })
      .finally(() => setLoading(false));
  };

  const handleIdInput = e => {
    if (e.target.value.length > 6 || isNaN(+e.target.value)) return;
    if (e.target.name === 'job') {
      setJobId(+e.target.value);
      setClientId('');
      return;
    }

    setClientId(+e.target.value);
    setJobId('');
  };

  const handleJobData = () => {
    if (jobId) {
      setLoading(true);
      api
        .SET_AND_GET_DATA_BY_JOB_ID({ ...formData }, jobId)
        .then(response => {
          if (response?.status && response.status === 'OK') {
            dispatch(addJob(response.data.job));
            dispatch(addClient(response.data.client));
          }
        })
        .finally(() => setLoading(false));
    }

    if (clientId) {
      setLoading(true);
      api
        .SET_AND_GET_DATA_BY_CLIENT_ID({ ...formData }, clientId)
        .then(response => {
          if (response?.status && response.status === 'OK') {
            dispatch(addClient(response.data.client));
          }
        })
        .finally(() => setLoading(false));
    }
  };

  // SECURITY: Helper function to normalize hours value to HH:mm format before parsing
  // This ensures that unmasked values (like "830", "1230") are formatted correctly
  const normalizeHoursToHHmm = (value) => {
    if (!value) return value;
    const strValue = String(value).trim();
    
    // If already in HH:mm format, return as-is
    if (strValue.includes(':')) {
      return strValue;
    }
    
    // If it's a decimal number (like "8.5", "11.25"), return as-is for getParsedHours to handle
    // getParsedHours will convert decimal to HH:mm format
    if (strValue.includes('.')) {
      return strValue;
    }
    
    // Remove any colons that might be incorrectly placed
    const cleanValue = strValue.replace(/:/g, '');
    
    // SECURITY: Check if this looks like an incorrectly stored value
    // If it's a 2-digit number > 24, it might be 8.5 stored as "85" (multiplied by 10)
    // Don't format it as "85:00" - let getParsedHours handle it as a decimal
    const numValue = parseFloat(cleanValue);
    if (cleanValue.length === 2 && numValue > 24 && numValue < 100) {
      // Likely 8.5 stored as 85, return as decimal string for proper conversion
      return (numValue / 10).toString();
    }
    
    // Format based on length
    if (cleanValue.length === 4) {
      // 4 digits: "1230" -> "12:30"
      return `${cleanValue.substring(0, 2)}:${cleanValue.substring(2, 4)}`;
    } else if (cleanValue.length === 3) {
      // 3 digits: "830" -> "08:30"
      return `0${cleanValue.substring(0, 1)}:${cleanValue.substring(1, 3)}`;
    } else if (cleanValue.length === 2 && numValue <= 24) {
      // 2 digits <= 24: "12" -> "12:00"
      return `${cleanValue}:00`;
    } else if (cleanValue.length === 1) {
      // 1 digit: "1" -> "01:00"
      return `0${cleanValue}:00`;
    }
    
    // If we can't format it, return as-is (might be decimal like "12.5")
    return strValue;
  };

  const getParsedHours = value => {
    // SECURITY: Handle both formatted (HH:mm) and unmasked (1230) values
    // Format unmasked values before parsing to prevent incorrect conversion
    // First normalize to HH:mm format
    const normalizedValue = normalizeHoursToHHmm(value);
    
    // Now parse the normalized value
    if (normalizedValue && normalizedValue.includes(':')) {
      const [hours, minutes] = normalizedValue.split(':');
      return +Number(
        +hours + (minutes == '0' || minutes == '00' ? 0 : +minutes / 60),
      ).toFixed(2);
    } else {
      // If still no colon, treat as decimal hours (e.g., "12.5")
      return +normalizedValue;
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);

    const day_shifts = (formData?.job?.booked_data?.day_shifts ?? []).map(el => {
      const { start_time, end_time } = el;
      if (!start_time && !end_time) return el;

      return {
        ...el,
        start:
          typeof start_time === 'string'
            ? start_time
            : new Date(
                new Date(start_time).getTime() -
                  new Date(start_time).getTimezoneOffset() * 60000,
              ),
        end:
          typeof end_time === 'string'
            ? end_time
            : new Date(
                new Date(end_time).getTime() -
                  new Date(end_time).getTimezoneOffset() * 60000,
              ),
        start_time:
          typeof start_time === 'string'
            ? start_time
            : new Date(
                new Date(start_time).getTime() -
                  new Date(start_time).getTimezoneOffset() * 60000,
              ),
        end_time:
          typeof end_time === 'string'
            ? end_time
            : new Date(
                new Date(end_time).getTime() -
                  new Date(end_time).getTimezoneOffset() * 60000,
              ),
        // SECURITY: Preserve hours field - convert string to decimal if needed
        // If hours is already a number, keep it. If it's a string like "11:45", convert to decimal.
        // If it's empty/null/undefined, preserve as-is (don't convert to 0 or NaN)
        hours:
          el.hours === null || el.hours === undefined || el.hours === ''
            ? el.hours
            : typeof el.hours === 'number'
            ? el.hours
            : getParsedHours(el.hours),
      };
    });

    const booked_dates = (formData?.job?.booked_data?.dates ?? []).map(el => {
      const { start, end, date } = el;
      if (!start && !end && !date) return el;

      return {
        ...el,
        date: new Date(
          new Date(date).getTime() - new Date(date).getTimezoneOffset() * 60000,
        ),
        ...(formData.job.booked_data.type === 'standard'
          ? {
              start_time:
                typeof start === 'string'
                  ? start
                  : new Date(
                      new Date(start).getTime() -
                        new Date(start).getTimezoneOffset() * 60000,
                    ),
              end_time:
                typeof end === 'string'
                  ? end
                  : new Date(
                      new Date(end).getTime() -
                        new Date(end).getTimezoneOffset() * 60000,
                    ),
            }
          : {
              start_time:
                typeof el?.start_time === 'string'
                  ? el?.start_time
                  : new Date(
                      new Date(el?.start_time).getTime() -
                        new Date(el?.start_time).getTimezoneOffset() * 60000,
                    ),
              end_time:
                typeof el?.end_time === 'string'
                  ? el?.end_time
                  : new Date(
                      new Date(el?.end_time).getTime() -
                        new Date(el?.end_time).getTimezoneOffset() * 60000,
                    ),
            }),
        start:
          typeof start === 'string'
            ? start
            : new Date(
                new Date(start).getTime() -
                  new Date(start).getTimezoneOffset() * 60000,
              ),
        end:
          typeof end === 'string'
            ? end
            : new Date(
                new Date(end).getTime() -
                  new Date(end).getTimezoneOffset() * 60000,
              ),
        // SECURITY: Preserve hours field - convert string to decimal if needed
        // If hours is already a number, keep it. If it's a string like "11:45", convert to decimal.
        // If it's empty/null/undefined, preserve as-is (don't convert to 0 or NaN)
        hours:
          el.hours === null || el.hours === undefined || el.hours === ''
            ? el.hours
            : typeof el.hours === 'number'
            ? el.hours
            : getParsedHours(el.hours),
      };
    });
    if (params.id) {
      await api.CHANGE_EDITING(params.id);
      dispatch(setEditedId(null));
    }

    api
      .SAVE_FORM_AS_DRAFT({
        ...formData,
        job: {
          ...formData.job,
          start_date: new Date(
            moment(formData.job.start_date)
              .set('h', 0)
              .set('m', 0)
              .set('s', 0)
              .toDate()
              .getTime() -
              new Date(formData.job.start_date).getTimezoneOffset() * 60000,
          ),
          end_date: new Date(
            moment(formData.job.end_date)
              .set('h', 0)
              .set('m', 0)
              .set('s', 0)
              .toDate()
              .getTime() -
              new Date(formData.job.end_date).getTimezoneOffset() * 60000,
          ),
          booked_data: {
            ...formData.job.booked_data,
            day_shifts,
            dates: booked_dates,
            levy: {
              enabled: levyEnabled,
              value: levyValue,
            },
          },
          files: files,
          notes: '' + notes,
        },
      })
      .then(status => {
        status === 'OK' &&
          saveAsDraftSuccess() &&
          navigate('/list-of-forms', { replace: true });
      })
      .finally(() => setLoading(false));
  };

  const handleCreatePlacement = async () => {
    // SECURITY: Validate dates before creating placement
    // Check for double booking and unavailable dates
    const calendar_data = formData?.job?.calendar_data;
    const allocatedDates = calendar_data?.applicant_allocated?.dates ?? [];
    const unavailableDates = calendar_data?.applicant_not_available?.dates ?? [];
    
    // Helper function to convert dates to milliseconds
    const toMs = (v) => {
      if (v instanceof Date) return v.getTime();
      if (typeof v === 'string') return Date.parse(v);
      if (v && typeof v.valueOf === 'function') return Number(v.valueOf());
      return Number(v) || NaN;
    };
    
    // Helper function to check for overlap
    const overlaps = (aStart, aEnd, bStart, bEnd) =>
      toMs(aStart) < toMs(bEnd) && toMs(aEnd) > toMs(bStart);
    
    // Get dates to validate (from booked_data.dates)
    const datesToValidate = formData.job.booked_data.dates || [];
    
    // Check for double booking
    const clashes = [];
    for (const sel of datesToValidate) {
      if (!sel.start || !sel.end) continue;
      for (const a of allocatedDates) {
        if (overlaps(sel.start, sel.end, a.start, a.end)) {
          clashes.push({
            selected: { start: sel.start, end: sel.end },
            existing: { start: a.start, end: a.end, title: a.title },
            type: 'double_booking',
          });
          break;
        }
      }
    }
    
    if (clashes.length) {
      const c = clashes[0];
      error({
        message: `Already booked: ${new Date(c.existing.start).toLocaleString()} → ${new Date(c.existing.end).toLocaleString()}`
      });
      setLoading(false);
      return;
    }
    
    // Check for unavailable dates
    const unavailableClashes = [];
    for (const sel of datesToValidate) {
      if (!sel.start || !sel.end) continue;
      for (const u of unavailableDates) {
        let unavailableStart = u.start;
        let unavailableEnd = u.end;
        
        // Check if unavailable date has valid start/end times
        const hasValidTimes = unavailableStart && unavailableEnd && 
                              !isNaN(toMs(unavailableStart)) && !isNaN(toMs(unavailableEnd));
        
        if (!hasValidTimes) {
          // If unavailable date doesn't have times, treat as entire day
          const unavailableDate = unavailableStart || unavailableEnd || u.date;
          if (unavailableDate) {
            const dateStr = moment(unavailableDate).format('YYYY-MM-DD');
            unavailableStart = moment(dateStr).startOf('day').toDate();
            unavailableEnd = moment(dateStr).endOf('day').toDate();
          } else {
            continue;
          }
        }
        
        if (overlaps(sel.start, sel.end, unavailableStart, unavailableEnd)) {
          unavailableClashes.push({
            selected: { start: sel.start, end: sel.end },
            existing: { start: unavailableStart, end: unavailableEnd, title: u.title },
            type: 'unavailable',
          });
          break;
        }
      }
    }
    
    if (unavailableClashes.length) {
      const c = unavailableClashes[0];
      error({
        message: `Applicant is not available: ${new Date(c.existing.start).toLocaleString()} → ${new Date(c.existing.end).toLocaleString()}`
      });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    let newRates = [...pay_rates];

    newRates = newRates.map(rate => {
      if (
        dates.find(
          shift => +shift.shift_external_id === +rate.shift_external_id,
        )
      ) {
        return rate;
      }

      return { ...rate, hidden: true };
    });

    newRates = newRates.filter(rate => {
      if (!rate.new) {
        return rate;
      } else if (rate.hidden) {
        return false;
      }

      return rate;
    });

    const day_shifts = formData.job.booked_data.day_shifts.map(el => {
      const { start_time, end_time } = el;
      if (!start_time && !end_time) return el;

      return {
        ...el,
        start:
          typeof start_time === 'string'
            ? start_time
            : new Date(
                new Date(start_time).getTime() -
                  new Date(start_time).getTimezoneOffset() * 60000,
              ),
        end:
          typeof end_time === 'string'
            ? end_time
            : new Date(
                new Date(end_time).getTime() -
                  new Date(end_time).getTimezoneOffset() * 60000,
              ),
        start_time:
          typeof start_time === 'string'
            ? start_time
            : new Date(
                new Date(start_time).getTime() -
                  new Date(start_time).getTimezoneOffset() * 60000,
              ),
        end_time:
          typeof end_time === 'string'
            ? end_time
            : new Date(
                new Date(end_time).getTime() -
                  new Date(end_time).getTimezoneOffset() * 60000,
              ),
      };
    });

    const booked_dates = formData.job.booked_data.dates.map(el => {
      const { start, end, date } = el;
      if (!start && !end && !date) return el;

      return {
        ...el,
        date: new Date(
          new Date(date).getTime() - new Date(date).getTimezoneOffset() * 60000,
        ),
        ...(formData.job.booked_data.type === 'standard'
          ? {
              start_time:
                typeof start === 'string'
                  ? start
                  : new Date(
                      new Date(start).getTime() -
                        new Date(start).getTimezoneOffset() * 60000,
                    ),
              end_time:
                typeof end === 'string'
                  ? end
                  : new Date(
                      new Date(end).getTime() -
                        new Date(end).getTimezoneOffset() * 60000,
                    ),
            }
          : {
              start_time:
                typeof el?.start_time === 'string'
                  ? el?.start_time
                  : new Date(
                      new Date(el?.start_time).getTime() -
                        new Date(el?.start_time).getTimezoneOffset() * 60000,
                    ),
              end_time:
                typeof el?.end_time === 'string'
                  ? el?.end_time
                  : new Date(
                      new Date(el?.end_time).getTime() -
                        new Date(el?.end_time).getTimezoneOffset() * 60000,
                    ),
            }),
        start:
          typeof start === 'string'
            ? start
            : new Date(
                new Date(start).getTime() -
                  new Date(start).getTimezoneOffset() * 60000,
              ),
        end:
          typeof end === 'string'
            ? end
            : new Date(
                new Date(end).getTime() -
                  new Date(end).getTimezoneOffset() * 60000,
              ),
        hours:
          typeof el.hours === 'number' ? el.hours : getParsedHours(el.hours),
      };
    });
    if (params.id) {
      await api.CHANGE_EDITING(params.id);
      dispatch(setEditedId(null));
    }
    // Normalize applicant.pay_type from object to integer before sending
    const normalizedApplicant = {
      ...formData.applicant,
      pay_type: formData.applicant?.pay_type?.id ?? 
                (typeof formData.applicant?.pay_type === 'number' ? formData.applicant.pay_type : null) ??
                formData.applicant?.pay_type,
    };

    api
      .CREATE_PLACEMENT({
        ...formData,
        applicant: normalizedApplicant,
        job: {
          ...formData.job,
          start_date: new Date(
            moment(formData.job.start_date)
              .set('h', 0)
              .set('m', 0)
              .set('s', 0)
              .toDate()
              .getTime() -
              new Date(formData.job.start_date).getTimezoneOffset() * 60000,
          ),
          end_date: new Date(
            moment(formData.job.end_date)
              .set('h', 0)
              .set('m', 0)
              .set('s', 0)
              .toDate()
              .getTime() -
              new Date(formData.job.end_date).getTimezoneOffset() * 60000,
          ),
          booked_data: {
            ...formData.job.booked_data,
            day_shifts,
            dates: booked_dates,
            levy: {
              enabled: levyEnabled,
              value: levyValue,
            },
          },
        },
      })
      .then(status => {
        if (status === 'OK') {
          // Set processing state and start polling for placement creation
          setPlacementProcessing(true);
          createSuccess();
          
          // Poll for placement creation (check every 3 seconds, max 20 times = 60 seconds)
          let pollCount = 0;
          const maxPolls = 20;
          placementPollIntervalRef.current = setInterval(() => {
            pollCount++;
            if (params.id) {
              // Check queue position
              api.GET_QUEUE_POSITION(params.id)
                .then(queueResponse => {
                  if (queueResponse?.status === 'OK' && queueResponse.data?.queue_position !== null) {
                    setQueuePosition(queueResponse.data.queue_position);
                  } else {
                    setQueuePosition(null);
                  }
                })
                .catch(() => {
                  setQueuePosition(null);
                });
              
              // Check form status
              api.SHOW_FORM(params.id)
                .then(response => {
                  if (response?.status === 'OK' && response.data) {
                    // Update form data
                    dispatch(addFormData(response.data));
                    
                    // Check if placement was created
                    if (response.data.placement_external_id) {
                      if (placementPollIntervalRef.current) {
                        clearInterval(placementPollIntervalRef.current);
                        placementPollIntervalRef.current = null;
                      }
                      setPlacementProcessing(false);
                      setQueuePosition(null);
                      setLoading(false);
                      // Navigate after placement is created
                      navigate('/list-of-forms', { replace: true });
                    } else if (response.data.status === 'error') {
                      // Placement creation failed
                      if (placementPollIntervalRef.current) {
                        clearInterval(placementPollIntervalRef.current);
                        placementPollIntervalRef.current = null;
                      }
                      setPlacementProcessing(false);
                      setQueuePosition(null);
                      setLoading(false);
                    }
                  }
                })
                .catch(() => {
                  if (placementPollIntervalRef.current) {
                    clearInterval(placementPollIntervalRef.current);
                    placementPollIntervalRef.current = null;
                  }
                  setPlacementProcessing(false);
                  setQueuePosition(null);
                  setLoading(false);
                });
            }
            
            // Stop polling after max attempts
            if (pollCount >= maxPolls) {
              if (placementPollIntervalRef.current) {
                clearInterval(placementPollIntervalRef.current);
                placementPollIntervalRef.current = null;
              }
              setPlacementProcessing(false);
              setQueuePosition(null);
              setLoading(false);
            }
          }, 3000);
        }
      })
      .catch(() => {
        setPlacementProcessing(false);
        setLoading(false);
      });
  };

  const handleGeneratePDF = only => {
    api.GET_DOCUMENTS(formData.id, only).then(
      res =>
        res.status === 'OK' &&
        res.data.map(doc =>
          api
            .DOWNLOAD_DOCUMENT(formData.id, { filename: doc.name })
            .then(response => {
              const url = window.URL.createObjectURL(
                new Blob([response], {
                  type: 'application/pdf;charset=utf-8',
                }),
              );
              const link = document.createElement('a');
              link.href = url;
              link.download = doc.download_name;
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
              link.remove();
            }),
        ),
    );
  };

  const handleEnterApplicant = e => e.key === 'Enter' && handleApplicantById();

  const handleEnterJobData = e => e.key === 'Enter' && handleJobData();

  const handleCheck = e =>
    setChecked({ ...checked, [e.target.name]: e.target.checked });

  const handleSendEmail = data => {
    const {
      recipient,
      subject,
      emails,
      cc_emails,
      bcc_emails,
      body,
      filesSend,
    } = data;
    return api
      .SEND_EMAIL(formData.id, {
        recipient,
        subject,
        emails,
        cc_emails,
        bcc_emails,
        body,
        files: filesSend,
      })
      .then(res => res === 'OK' && emailSendSuccess([recipient]))
      .catch(err => error(err));
  };

  const handleSendEmailModal = () => {
    api.GET_EMAIL_DATA(formData.id).then(data => {
      setEmailData(data?.data ?? {});
    });
    setShowEmailModal(!showEmailModal);
  };

  return (
    <main className={css.main}>
      {formData.edited_by && formData.edited_by !== user.id && !loading && (
        <div className={css.headerNotificationContainer}>
          <div className={css.headerNotification}>
            <p className={css.headerNotificationText}>
              This form is currently being edited by the{' '}
              {formData.edited_by_user}
            </p>
          </div>
        </div>
      )}
      <header>
        <div className={css.flex1}>
          <div>
            <div className={css.inputHeader}>
              <Input
                type="text"
                placeholder="Enter Applicant ID"
                onChange={handleApplicantIdChange}
                onKeyDown={handleEnterApplicant}
                value={id}
                autoFocus
                readOnly={formData.status === 'archive'}
                required
              />
            </div>
            {!formData.placement_external_id && (
              <div className={css.btnContainer}>
                <Button
                  type="secondary"
                  title="Get applicant data"
                  onClick={handleApplicantById}
                />
              </div>
            )}
          </div>
          {workHistory.length === 0 && (
            <Select
              size="x33"
              value={previousJob || workHistory[0]?.placement_external_id}
              options={
                workHistory.length > 0 &&
                workHistory.map(el => {
                  return {
                    key: el.placement_external_id,
                    title: el.full_description,
                  };
                })
              }
              defaultValue="Previous Applicant Job"
              onChange={handleJobChange}
              disabled={formData.status === 'archive'}
            />
          )}
          {(!formData?.status ||
            formData?.status === 'draft' ||
            formData?.status === 'new' ||
            formData?.status === 'error') && (
            <Button
              type={!formData?.applicant?.external_id || !job?.is_agree ? 'disabled2' : 'primary'}
              title="Save as draft"
              onClick={handleSaveDraft}
              disabled={!formData?.applicant?.external_id || !job?.is_agree}
            />
          )}
        </div>
        {workHistory.length > 0 && (
          <Select
            size="x70"
            title={true}
            value={previousJob || workHistory[0]?.placement_external_id}
            options={
              workHistory.length > 0 &&
              workHistory.map(el => {
                return {
                  key: el.placement_external_id,
                  title: el.full_description,
                };
              })
            }
            defaultValue="Previous Applicant Job"
            onChange={handleJobChange}
            disabled={formData.status === 'archive'}
          />
        )}
        {loading && (
          <div className={css.loader}>
            <Oval {...props} />
          </div>
        )}
        {formData?.applicant?.external_id && !loading && (
          <div className={css.flex2}>
            <div>
              <div className={css.inputHeader}>
                <Input
                  value={jobId || ''}
                  name="job"
                  type="text"
                  placeholder="Job ID"
                  onChange={handleIdInput}
                  onKeyDown={handleEnterJobData}
                  readOnly={formData.status === 'archive'}
                />
              </div>
              <Input
                value={clientId || ''}
                type="text"
                placeholder="Client ID"
                onChange={handleIdInput}
                onKeyDown={handleEnterJobData}
                readOnly={formData.status === 'archive'}
              />
            </div>
            {!formData.placement_external_id ? (
              <Button
                type="secondary"
                title="Get job data"
                onClick={handleJobData}
              />
            ) : (
              <Button
                type={'secondary'}
                style={{ minWidth: 170 }}
                onClick={handleSendEmailModal}
              >
                Send e-mail
              </Button>
            )}
          </div>
        )}
      </header>
      {formData?.applicant?.external_id && !loading && (
        <main className={css.mainForm}>
          <div className={css.select}>
            {(formData.applicant?.consultants ?? []).map((el, i) => (
              <Input
                readOnly
                key={el.external_id}
                value={`${el.first_name} ${el.last_name}`}
                size="x50"
                type="text"
                placeholder={`${i + 1}${i === 0 ? 'st' : 'nd'} RC Full Name`}
                required
              />
            ))}
          </div>
          <ApplicantSection 
            onValidationChange={setHasApplicantErrors} 
          />
          <JobSection 
            id={params.id} 
            isAvailability={isAvailability} 
            onValidationChange={setHasJobErrors}
          />
          <ClientSection 
            onValidationChange={setHasClientErrors}
          />
          {(!formData?.status ||
            formData?.status === 'draft' ||
            formData?.status === 'error' ||
            (formData?.status === 'new' && !formData?.placement_external_id) ||
            (formData?.status === 'processing' && !formData?.placement_external_id)) && (
            <div className={css.hr}>
              <Button
                disabled={
                  !job.is_agree || 
                  hasAnyErrors || 
                  placementProcessing || 
                  formData?.status === 'processing'
                }
                type={
                  !job.is_agree || 
                  hasAnyErrors || 
                  placementProcessing || 
                  formData?.status === 'processing'
                    ? 'primaryBigDisabled' 
                    : 'primaryBig'
                }
                title={
                  placementProcessing || formData?.status === 'processing'
                    ? 'Creating Placement...'
                    : 'Create Placement'
                }
                onClick={handleCreatePlacement}
              />
              {(placementProcessing || formData?.status === 'processing') && (
                <p style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '14px' }}>
                  Placement is being created. Please wait...
                  {queuePosition !== null && queuePosition > 0 && (
                    <span style={{ display: 'block', marginTop: '5px', fontWeight: 'bold' }}>
                      Queue position: {queuePosition}
                    </span>
                  )}
                  {queuePosition === 0 && (
                    <span style={{ display: 'block', marginTop: '5px', fontWeight: 'bold', color: '#28a745' }}>
                      Currently processing...
                    </span>
                  )}
                </p>
              )}
            </div>
          )}
          {/* SECURITY: Show error/notes field when form has error status OR when placement exists */}
          {/* This ensures users can see error messages even when placement creation failed */}
          {(formData.status === 'error' || formData.placement_external_id) && (
            <>
              <div className={css.flex3}>
                {formData.placement_external_id && (
                  <>
                <Input
                  readOnly
                  value={formData.completed_by_user}
                  size="x50"
                  type="text"
                  placeholder="Completed By"
                />
                <Input
                  readOnly
                  value={moment(formData.created_on).format('DD/MM/YYYY HH:mm')}
                  size="x50"
                  type="text"
                  placeholder="Timestamp"
                />
                <Input
                  readOnly
                  value={formData.placement_external_id}
                  size="x50"
                  type="text"
                  placeholder="Placement ID"
                />
                  </>
                )}
                <Input
                  readOnly
                  value={formData.notes || ''}
                  size={formData.placement_external_id ? "x50" : "x100"}
                  type="text"
                  placeholder={formData.status === 'error' ? "Error Details" : "Errors/Notes"}
                  style={formData.status === 'error' ? { borderColor: '#e53935', backgroundColor: '#ffebee' } : {}}
                />
              </div>
              {/*{(formData?.document_status === 'done' ||*/}
              {/*  formData?.document_status === 'error') && (*/}
              {/*  <div className={css.pdf}>*/}
              {/*    <Button*/}
              {/*      title='Generate PDFs Documents'*/}
              {/*      type={*/}
              {/*        !checked.applicant && !checked.client*/}
              {/*          ? 'disabled'*/}
              {/*          : 'secondary'*/}
              {/*      }*/}
              {/*      disabled={!checked.applicant && !checked.client}*/}
              {/*      onClick={handleGeneratePDF}*/}
              {/*    />*/}

              {/*    <label className={css.label}>*/}
              {/*      <input*/}
              {/*        name='applicant'*/}
              {/*        className={css.checkbox}*/}
              {/*        type='checkbox'*/}
              {/*        checked={checked.applicant}*/}
              {/*        onChange={handleCheck}*/}
              {/*      />*/}
              {/*      Applicant*/}
              {/*    </label>*/}
              {/*    <label className={css.label}>*/}
              {/*      <input*/}
              {/*        name='client'*/}
              {/*        className={css.checkbox}*/}
              {/*        type='checkbox'*/}
              {/*        checked={checked.client}*/}
              {/*        onChange={handleCheck}*/}
              {/*      />*/}
              {/*      Client*/}
              {/*    </label>*/}
              {/*  </div>*/}
              {/*)}*/}
            </>
          )}
        </main>
      )}
      <SendEmailModal
        showEmailModal={showEmailModal}
        onCloseModal={handleSendEmailModal}
        onSendEmail={handleSendEmail}
        emailData={emailData}
        formDataId={formData.id}
        onGeneratePdf={handleGeneratePDF}
      />
    </main>
  );
};

export default BookingFormPage;
